import AddIcon from '@mui/icons-material/Add'
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined'
import { Box, ButtonBase, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { offeredSwatches, sectionMarks } from '../../shared/catalogue-diff'
import type { Change } from '../../shared/catalogue-diff'
import { previewOptionsFor } from '../../shared/compose-avatar'
import { stackLayers, unlocksAtTier } from '../../shared/layer-stack'
import type { BoCatalog, BoSection, LayerUrls } from '../../shared/types'
import PreviewTile, { TILE } from './PreviewTile'

export interface PreviewOptionStripProps {
  catalogue: BoCatalog
  section: BoSection
  tier: number
  changes?: Change[]
  selectedSlug?: string
  selectedSwatch?: string
  onPickOption: (slug: string) => void
  onPickSwatch: (swatch: string) => void
  onAddOption?: () => void
}

const sameHex = (a: string, b: string) =>
  a.replace(/^#/, '').toUpperCase() === b.replace(/^#/, '').toUpperCase()

export default function PreviewOptionStrip({
  catalogue,
  section,
  tier,
  changes,
  selectedSlug,
  selectedSwatch,
  onPickOption,
  onPickSwatch,
  onAddOption,
}: PreviewOptionStripProps) {
  const { formatMessage } = useIntl()
  const isColour = section.kind === 'color'
  const marks = sectionMarks(changes, section.id)

  const swatches = isColour
    ? offeredSwatches(section.swatches, section.retiredSwatches)
    : []
  const removedSwatches = isColour
    ? marks.removed.filter((hex) => !swatches.some((s) => sameHex(s, hex)))
    : []
  const listed = isColour ? [] : previewOptionsFor(catalogue, section.id, tier)
  const options = listed.filter((o) => o.option.status === 'active')
  // Retired pictures stay visible - an admin may want to look at one - but as removed, never as a
  // pick. Names the diff knows and the catalogue does not get a tile without artwork.
  const removedOptions = isColour
    ? []
    : [
        ...listed
          .filter((o) => o.option.status !== 'active')
          .map((o) => ({ slug: o.option.slug, option: o.option })),
        ...marks.removed
          .filter((slug) => !listed.some((o) => o.option.slug === slug))
          .map((slug) => ({ slug, option: undefined })),
      ]
  const empty =
    swatches.length +
      removedSwatches.length +
      options.length +
      removedOptions.length ===
    0

  const artwork = (layers: LayerUrls) =>
    stackLayers(layers).map((layer) => (
      <img
        key={layer.role}
        src={layer.url}
        alt=""
        aria-hidden={true}
        width={TILE - 12}
        height={TILE - 12}
        loading="lazy"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          mixBlendMode: layer.blend,
        }}
      />
    ))

  const swatchFill = (hex: string) => (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 0.5,
        bgcolor: `#${hex}`,
      }}
    />
  )

  return (
    <Box
      role="group"
      data-testid="preview-strip"
      aria-label={formatMessage(
        { id: 'avatarStudio.preview.stripAria' },
        { section: section.displayName ?? section.id },
      )}
      sx={{
        display: 'flex',
        gap: 1,
        overflowX: 'auto',
        overscrollBehavior: 'contain',
        touchAction: 'manipulation',
        py: 1,
      }}
    >
      {empty && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {formatMessage({ id: 'avatarStudio.preview.emptySection' })}
        </Typography>
      )}

      {swatches.map((swatch, index) => {
        // Positional under a count ladder, and the API counts offered swatches only - so the index
        // here is among those, or the badge disagrees with the tier a player actually gets.
        const unlocksAt = unlocksAtTier(section.tierRule, index, swatch)
        const locked =
          unlocksAt === null ? Boolean(section.tierRule) : unlocksAt > tier

        return (
          <PreviewTile
            key={swatch}
            testId={`preview-swatch-${swatch}`}
            label={swatch}
            ariaLabel={formatMessage(
              { id: 'avatarStudio.editor.swatches.aria' },
              { hex: swatch },
            )}
            selected={swatch === selectedSwatch}
            onClick={() => onPickSwatch(swatch)}
            dimArtwork={locked}
            unlocksAt={unlocksAt}
            locked={locked}
            mark={marks.added.has(swatch) ? 'new' : undefined}
          >
            {swatchFill(swatch)}
          </PreviewTile>
        )
      })}

      {options.map(({ option, unlocksAt, lockedAtTier }) => (
        <PreviewTile
          key={option.slug}
          testId={`preview-option-${option.slug}`}
          label={option.displayName}
          selected={option.slug === selectedSlug}
          onClick={() => onPickOption(option.slug)}
          dimArtwork={lockedAtTier}
          unlocksAt={unlocksAt}
          locked={lockedAtTier}
          mark={marks.added.has(option.slug) ? 'new' : undefined}
        >
          {artwork(option.layers)}
        </PreviewTile>
      ))}

      {removedSwatches.map((hex) => (
        <PreviewTile
          key={`removed-${hex}`}
          testId={`preview-removed-${hex}`}
          label={hex}
          ariaLabel={formatMessage(
            { id: 'avatarStudio.preview.removedTileAria' },
            { name: hex },
          )}
          mark="removed"
        >
          {swatchFill(hex)}
        </PreviewTile>
      ))}

      {removedOptions.map(({ slug, option }) => (
        <PreviewTile
          key={`removed-${slug}`}
          testId={`preview-removed-${slug}`}
          label={option?.displayName ?? slug}
          ariaLabel={formatMessage(
            { id: 'avatarStudio.preview.removedTileAria' },
            { name: option?.displayName ?? slug },
          )}
          mark="removed"
        >
          {option ? (
            artwork(option.layers)
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                borderRadius: 0.5,
              }}
            >
              <HideImageOutlinedIcon color="disabled" />
            </Box>
          )}
        </PreviewTile>
      ))}

      {onAddOption && (
        <ButtonBase
          onClick={onAddOption}
          sx={{
            width: TILE,
            height: TILE + 28,
            borderRadius: 1,
            border: '2px dashed',
            borderColor: 'divider',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
          }}
          data-testid="preview-add-option"
        >
          <AddIcon color="action" />
          <Typography variant="caption">
            {formatMessage({
              id: isColour
                ? 'avatarStudio.grid.addColour'
                : 'avatarStudio.wizard.addOption',
            })}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  )
}
